import { useState, useEffect, useCallback, useMemo } from 'react';
import { Toast } from '@douyinfe/semi-ui';
import { API } from '../helpers';

const APIKEY_STORAGE_KEY = 'neko_apikey';
const QUOTA_PER_UNIT = 500000;
const UNLIMITED_BALANCE = 100000000;

const DEFAULT_BASE_URLS = {
    '线路一': 'https://cc.jispul.com',
    '线路二': 'https://rb.jispul.com',
};

function parseBaseUrls() {
    try {
        return JSON.parse(process.env.REACT_APP_BASE_URL || JSON.stringify(DEFAULT_BASE_URLS));
    } catch (_) {
        return DEFAULT_BASE_URLS;
    }
}

function extractLogs(payload) {
    if (Array.isArray(payload)) return payload;
    if (payload && typeof payload === 'object') {
        if (Array.isArray(payload.items)) return payload.items;
        if (Array.isArray(payload.data)) return payload.data;
    }
    return [];
}

function normalizeLogs(logs) {
    return logs.map(log => ({
        ...log,
        model_name: log.model_name || log.model || '',
        quota: typeof log.quota === 'number' ? log.quota / QUOTA_PER_UNIT : 0,
    }));
}
export const useTokenQuery = () => {
    const [apikey, setAPIKey] = useState(() => {
        return localStorage.getItem(APIKEY_STORAGE_KEY) || '';
    });
    
    const [loading, setLoading] = useState(false);
    const [logsLoading, setLogsLoading] = useState(false);
    const [activeTabKey, setActiveTabKey] = useState('');
    const [baseUrl, setBaseUrl] = useState('');
    const [logPage, setLogPage] = useState(1);
    const [logTotal, setLogTotal] = useState(0);
    const logPageSize = 100;
    const [tabData, setTabData] = useState({});
    const baseUrls = useMemo(() => parseBaseUrls(), []);

    // 初始化 BaseUrl
    useEffect(() => {
        const keys = Object.keys(baseUrls);
        if (keys.length > 0) {
            const firstKey = keys[0];
            setActiveTabKey(firstKey);
            setBaseUrl(baseUrls[firstKey]);
        }
    }, [baseUrls]);

    const handleTabChange = (key) => {
        setActiveTabKey(key);
        setBaseUrl(baseUrls[key]);
        setLogPage(1);
        setLogTotal(0);
    };

    const resetData = useCallback((key) => {
        setTabData((prevData) => ({
            ...prevData,
            [key]: {
                balance: 0,
                usage: 0,
                accessdate: "未知",
                logs: [],
                tokenValid: false,
            }
        }));
    }, []);

    const fetchData = async () => {
        if (apikey === '') {
            Toast.warning('请先输入令牌，再进行查询');
            return;
        }
        // 简单格式校验
        if (!/^sk-[a-zA-Z0-9]{48}$/.test(apikey)) {
            Toast.error('令牌格式非法！');
            return;
        }

        setLoading(true);
        // 初始化当前 tab 的数据
        let newTabData = {
            ...tabData[activeTabKey],
            balance: 0,
            usage: 0,
            accessdate: 0,
            logs: [],
            tokenValid: false
        };

        try {
            if (process.env.REACT_APP_SHOW_BALANCE === "true") {
                const usageRes = await API.get(`${baseUrl}/api/usage/token/`, {
                    headers: { Authorization: `Bearer ${apikey}` },
                });
                const usagePayload = usageRes?.data || {};
                if (!usagePayload.code || !usagePayload.data) {
                    throw new Error(usagePayload.message || 'Token usage query failed');
                }

                const usageData = usagePayload.data;
                newTabData.balance = usageData.unlimited_quota ? UNLIMITED_BALANCE : Number(usageData.total_granted || 0) / QUOTA_PER_UNIT;
                newTabData.usage = Number(usageData.total_used || 0) / QUOTA_PER_UNIT;
                newTabData.accessdate = usageData.expires_at === -1 ? 0 : usageData.expires_at;
                newTabData.tokenValid = true;
                newTabData.name = usageData.name;

                const balanceRes = await API.get(`${baseUrl}/usage/api/balance`, {
                    headers: { Authorization: `Bearer ${apikey}` },
                });
                const balancePayload = balanceRes?.data || {};
                if (balancePayload.success && balancePayload.data) {
                    newTabData.status = balancePayload.data.status;
                    newTabData.statusText = balancePayload.data.status_text;
                    newTabData.loginToken = balancePayload.data.login_token;
                    if (usageData.unlimited_quota) {
                        newTabData.balance = UNLIMITED_BALANCE;
                    }
                }
                localStorage.setItem(APIKEY_STORAGE_KEY, apikey);
            }
        } catch (e) {
            console.error(e);
            Toast.error("令牌无效或已用尽");
            resetData(activeTabKey);
            setLoading(false);
            return;
        }

        // ★ 余额查完立即渲染，不等日志
        setTabData((prevData) => ({
            ...prevData,
            [activeTabKey]: newTabData,
        }));
        setLoading(false);

        // ★ 日志分页加载（不阻塞余额显示）
        if (process.env.REACT_APP_SHOW_DETAIL === "true") {
            loadLogs(activeTabKey, 1);
        }
    };

    const loadLogs = async (tabKey, page, url) => {
        const targetUrl = url || baseUrl;
        if (!apikey || !targetUrl) return;
        try {
            setLogsLoading(true);
            const logRes = await API.get(
                `${targetUrl}/api/log/token?p=${page}&page_size=${logPageSize}`,
                { headers: { Authorization: `Bearer ${apikey}` } },
            );
            const resp = logRes.data || {};
            if (resp.success) {
                const payload = resp.data;
                if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
                    const logs = normalizeLogs(extractLogs(payload));
                    setLogTotal(payload.total || 0);
                    setLogPage(payload.page || page);
                    setTabData(prev => ({
                        ...prev,
                        [tabKey]: { ...prev[tabKey], logs },
                    }));
                } else {
                    const logs = normalizeLogs(extractLogs(payload));
                    setLogTotal(logs.length);
                    setLogPage(1);
                    setTabData(prev => ({
                        ...prev,
                        [tabKey]: { ...prev[tabKey], logs },
                    }));
                }
            }
        } catch (e) {
            console.error(e);
            Toast.warning("日志查询失败");
        } finally {
            setLogsLoading(false);
        }
    };

    const handleLogPageChange = (page) => {
        loadLogs(activeTabKey, page, baseUrl);
    };

    // 全量加载所有日志（分页循环拉取，用于图表）
    const [allLogs, setAllLogs] = useState([]);
    const [allLogsLoading, setAllLogsLoading] = useState(false);
    const [allLogsCooldown, setAllLogsCooldown] = useState(false);

    const loadAllLogs = async () => {
        if (!apikey || !baseUrl || allLogsLoading || allLogsCooldown) return;
        setAllLogsLoading(true);
        try {
            const batchSize = 100;
            // 第一页：拿 total
            const firstRes = await API.get(
                `${baseUrl}/api/log/token?p=1&page_size=${batchSize}`,
                { headers: { Authorization: `Bearer ${apikey}` } },
            );
            const firstResp = firstRes.data || {};
            if (!firstResp.success) throw new Error('加载失败');

            const payload = firstResp.data;
            let collected = normalizeLogs(extractLogs(payload));
            const total = (payload && payload.total) || collected.length;
            const totalPages = Math.ceil(total / batchSize);

            // 并发拉剩余页（最多 50 页 = 5000 条，防止过载）
            const maxPages = Math.min(totalPages, 50);
            if (maxPages > 1) {
                const promises = [];
                for (let p = 2; p <= maxPages; p++) {
                    promises.push(
                        API.get(
                            `${baseUrl}/api/log/token?p=${p}&page_size=${batchSize}`,
                            { headers: { Authorization: `Bearer ${apikey}` } },
                        ).catch(() => null)
                    );
                }
                const results = await Promise.all(promises);
                for (const res of results) {
                    if (!res) continue;
                    const r = res.data || {};
                    if (r.success && r.data) {
                        collected = collected.concat(normalizeLogs(extractLogs(r.data)));
                    }
                }
            }

            setAllLogs(collected);
            Toast.success(`已加载 ${collected.length} / ${total} 条日志`);

            // 30 秒冷却
            setAllLogsCooldown(true);
            setTimeout(() => setAllLogsCooldown(false), 30000);
        } catch (e) {
            console.error(e);
            Toast.error("加载全部日志失败");
        } finally {
            setAllLogsLoading(false);
        }
    };

    const activeTabData = tabData[activeTabKey] || { 
        logs: [], 
        balance: 0, 
        usage: 0, 
        accessdate: "未知", 
        tokenValid: false 
    };

    return {
        apikey,
        setAPIKey,
        loading,
        logsLoading,
        baseUrls,
        activeTabKey,
        handleTabChange,
        fetchData,
        activeTabData,
        logPage,
        logTotal,
        logPageSize,
        handleLogPageChange,
        allLogs,
        allLogsLoading,
        allLogsCooldown,
        loadAllLogs,
    };
};

